import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Button as Button95, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { Link, RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Image } from 'expo-image';
import { Meta } from '@/components/Meta';
import { PasswordShowHideButton } from '@/components/PasswordShowHideButton';
import { PasswordRequirementsButton } from '@/components/PasswordRequirementsButton';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
});

export default function SetLogin() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const emailInput = useRef<RNTextInput>(null);
  const passwordInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const [newPasswordShown, setNewPasswordShown] = useState(false);

  const { register, control, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    }
  });

  const [email, password] = watch(["email", "password"]);

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();
      setError("");

      const response = await AppUtilities.fetchAuthorized(
        "SetLogin.onSubmit",
        "/rabbl/api/set_login",
        "POST",
        {
          domain: domain,
          email: data.email,
          password: data.password,
        }
      );

      const loggedIn: boolean | undefined = ((response as any).body as any).loggedIn;
      if (loggedIn) {
        AppUtilities.login((response as any).body);
      }

      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "SetLogin.onSubmit");
      setError(AppUtilities.getErrors(e));
    } finally {
      hideSpinner();
    }
  }, [domain]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (emailInput && emailInput.current) {
        emailInput.current?.focus();
      }
    }
  }, [emailInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Email and password"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Create an account or login:</AppText>

          <Controller
            control={control}
            name="email"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={emailInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Email"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  autoComplete="email"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  onSubmitEditing={() => passwordInput.current?.focus()}
                  autoCapitalize='none'
                />
                {AppUtils.PASSWORD_REQUIREMENTS_BLANK}
              </View>
            )}
          />
          {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Email is required.</AppText>}

          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={passwordInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Password"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  autoComplete="password"
                  textContentType="password"
                  secureTextEntry={newPasswordShown ? false : true}
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                />
                <PasswordRequirementsButton />
                <PasswordShowHideButton passwordShown={newPasswordShown} setPasswordShown={setNewPasswordShown} />
              </View>
            )}
          />
          {errors.password && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Password is required.</AppText>}

          <View style={styles.inputWrapper}>
            <Button95
              onPress={handleSubmit(onSubmit, onValidationErrors)}
              disabled={email == "" || password == ''}
            >Next</Button95>
          </View>

          { (error && error.length > 0) ?
                (
                  <>
                    <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>{error}</AppText>

                    {
                      error.indexOf("password did not match") != -1 &&
                        <Link
                          href={{
                            pathname: "/forgotpassword",
                            params: {
                              e: email,
                            },
                          }}
                          asChild={true}
                          className={"nontext"}
                        >
                          <Button95>Forgot password?</Button95>
                        </Link>
                    }
                  </>
                )
              :
                null
          }
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
